import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
  OutlinedInput,
  CircularProgress
} from '@mui/material';

import { useAuth } from '../../../contexts/AuthContext';
import { searchTenantUsersAdmin } from '../../../api/backendApi';

const ROLE_OPTIONS = [
  'admin',
  'superadmin',
  'tenant_admin',
  'responder',
  'overseer',
  'viewer'
];

const AssignmentEditorDialog = ({ open, assignment, onClose, onSave }) => {
  const { userData, tenantId: authTenantId } = useAuth();
  const tenantId = authTenantId || userData?.tenantId;
  const [formData, setFormData] = useState({
    targetKind: 'role',
    targetValue: '',
    targetValues: [], // For multi-select roles
    priority: 0,
    autoOpen: true
  });

  const [errors, setErrors] = useState({});

  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  useEffect(() => {
    if (assignment) {
      setFormData({
        targetKind: assignment.targetKind || 'role',
        targetValue: assignment.targetValue || '',
        targetValues: assignment.targetKind === 'role' ? [assignment.targetValue] : [],
        priority: assignment.priority || 0,
        autoOpen: assignment.autoOpen !== undefined ? assignment.autoOpen : true
      });
    } else {
      // Reset for new assignment
      setFormData({
        targetKind: 'role',
        targetValue: '',
        targetValues: [],
        priority: 0,
        autoOpen: true
      });
    }
    setErrors({});
    setUserSearchTerm('');
    setUserSearchResults([]);
    setUserSearchLoading(false);
  }, [assignment, open]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      if (field === 'targetKind') {
        const nextKind = value;
        return {
          ...prev,
          targetKind: nextKind,
          targetValue: '',
          targetValues: [],
        };
      }

      return {
        ...prev,
        [field]: value
      };
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    let active = true;
    const runSearch = async () => {
      const term = (userSearchTerm || '').trim();
      if (!open || formData.targetKind !== 'user' || !tenantId || term.length < 2) {
        if (active) {
          setUserSearchResults([]);
          setUserSearchLoading(false);
        }
        return;
      }

      setUserSearchLoading(true);
      try {
        const results = await searchTenantUsersAdmin(tenantId, term, 25);
        if (!active) return;
        setUserSearchResults(Array.isArray(results) ? results : []);
      } catch (_) {
        if (!active) return;
        setUserSearchResults([]);
      } finally {
        if (active) setUserSearchLoading(false);
      }
    };

    const id = setTimeout(runSearch, 300);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [open, tenantId, userSearchTerm, formData.targetKind]);

  const validate = () => {
    const newErrors = {};

    if (!formData.targetKind) {
      newErrors.targetKind = 'Target kind is required';
    }

    if (formData.targetKind === 'role' && formData.targetValues.length === 0) {
      newErrors.targetValues = 'At least one role must be selected';
    }

    if (formData.targetKind === 'user' && !formData.targetValue.trim()) {
      newErrors.targetValue = 'User ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    // For role assignments, we can save multiple assignments at once
    if (formData.targetKind === 'role' && formData.targetValues.length > 0) {
      // If editing, just save the single assignment
      if (assignment) {
        onSave({
          targetKind: 'role',
          targetValue: formData.targetValues[0],
          priority: parseInt(formData.priority, 10),
          autoOpen: formData.autoOpen
        });
      } else {
        // For new assignments, save the first one (UI can be enhanced to save multiple)
        onSave({
          targetKind: 'role',
          targetValue: formData.targetValues[0],
          priority: parseInt(formData.priority, 10),
          autoOpen: formData.autoOpen
        });
      }
    } else {
      onSave({
        targetKind: formData.targetKind,
        targetValue: formData.targetValue,
        priority: parseInt(formData.priority, 10),
        autoOpen: formData.autoOpen
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {assignment ? 'Edit Default Assignment' : 'Add Default Assignment'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Target Kind</InputLabel>
            <Select
              value={formData.targetKind}
              onChange={(e) => handleChange('targetKind', e.target.value)}
              label="Target Kind"
              error={!!errors.targetKind}
            >
              <MenuItem value="role">Role</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>

          {formData.targetKind === 'role' ? (
            <FormControl fullWidth required error={!!errors.targetValues}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple={!assignment} // Allow multi-select only for new assignments
                value={assignment ? [formData.targetValues[0] || ''] : formData.targetValues}
                onChange={(e) => handleChange('targetValues', typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(Array.isArray(selected) ? selected : [selected]).map((value) => (
                      value && <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              {errors.targetValues && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }}>
                  {errors.targetValues}
                </Box>
              )}
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Search users by name or email"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  helperText="Type at least 2 characters to search"
                  fullWidth
                />
                {userSearchLoading && <CircularProgress size={20} />}
              </Box>

              {userSearchResults.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 160,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {userSearchResults.map((u) => {
                    const uid = u?.id;
                    if (!uid) return null;
                    const name = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ');
                    const primary = name || u.email || uid;
                    const already = formData.targetValue === uid;
                    return (
                      <Box
                        key={uid}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.5,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleChange('targetValue', uid)}
                      >
                        <Checkbox size="small" checked={already} sx={{ mr: 1 }} />
                        <Box sx={{ flex: 1 }}>
                          <div style={{ fontSize: 14 }}>{primary}</div>
                          {u.email && primary !== u.email && (
                            <div style={{ fontSize: 11, color: '#6B7280' }}>{u.email}</div>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <TextField
                label="Selected User ID"
                value={formData.targetValue}
                onChange={(e) => handleChange('targetValue', e.target.value)}
                error={!!errors.targetValue}
                helperText={errors.targetValue || 'This is the user uid that will be stored'}
                required
                fullWidth
              />
            </Box>
          )}

          <TextField
            label="Priority"
            type="number"
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
            helperText="Higher priority assignments are shown first (default: 0)"
            inputProps={{ min: 0, max: 100 }}
            fullWidth
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.autoOpen}
                onChange={(e) => handleChange('autoOpen', e.target.checked)}
              />
            }
            label="Auto-Open Panel"
          />
          <Box sx={{ ml: 4, color: 'text.secondary', fontSize: '0.875rem' }}>
            Automatically open the incident panel when this assignment is created
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentEditorDialog;
